/**
 * Detects browser, OS, and device type from `navigator` and adds classes to `<body>`.
 *
 * Classes (one per category where applicable):
 * - `browser-{chrome|firefox|safari|edge|opera|unknown}`
 * - `os-{windows|macos|linux|android|ios|unknown}`
 * - `device-{mobile|tablet|desktop}`
 * - `is-touch` when a coarse pointer / touch is available
 * - `is-ios` for iPhone, iPad, iPod (including iPadOS desktop UA)
 */

const BROWSER_CLASS_PREFIX = "browser-";
const OS_CLASS_PREFIX = "os-";
const DEVICE_CLASS_PREFIX = "device-";

/**
 * @param {string} ua
 * @returns {"chrome"|"firefox"|"safari"|"edge"|"opera"|"unknown"}
 */
export function detectBrowser(ua) {
  if (/EdgA?\/|EdgiOS\//i.test(ua)) return "edge";
  if (/OPR\/|Opera/i.test(ua)) return "opera";
  if (/Firefox\/|FxiOS\//i.test(ua)) return "firefox";
  if (/CriOS\/|Chrome\/|Chromium\//i.test(ua) && !/Edg/i.test(ua)) return "chrome";
  if (/Safari\//i.test(ua) && !/Chrome|Chromium|CriOS/i.test(ua)) return "safari";
  return "unknown";
}

/**
 * @param {string} ua
 * @returns {"windows"|"macos"|"linux"|"android"|"ios"|"unknown"}
 */
export function detectOs(ua) {
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (
    typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    navigator.maxTouchPoints > 1
  ) {
    return "ios";
  }
  if (/Android/i.test(ua)) return "android";
  if (/Win(dows|32|64|CE)|Windows Phone/i.test(ua)) return "windows";
  if (/Mac OS X|Macintosh/i.test(ua)) return "macos";
  if (/Linux|CrOS/i.test(ua)) return "linux";
  return "unknown";
}

/**
 * @param {string} ua
 * @returns {boolean}
 */
export function isIosDevice(ua) {
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return (
    typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    navigator.maxTouchPoints > 1
  );
}

/**
 * @param {string} ua
 * @returns {"mobile"|"tablet"|"desktop"}
 */
export function detectDeviceType(ua) {
  const ios = isIosDevice(ua);
  if (ios) {
    if (/iPad/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
      return "tablet";
    }
    return "mobile";
  }

  if (/Android/i.test(ua)) {
    return /Mobile/i.test(ua) ? "mobile" : "tablet";
  }

  if (/Mobi|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return "mobile";
  }

  if (/Tablet|PlayBook/i.test(ua)) {
    return "tablet";
  }

  return "desktop";
}

/**
 * @returns {boolean}
 */
export function hasTouchInput() {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia === "function") {
    if (window.matchMedia("(pointer: coarse)").matches) return true;
    if (window.matchMedia("(hover: none)").matches) return true;
  }
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

/**
 * @returns {string[]}
 */
export function getDeviceBrowserBodyClasses() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const classes = [
    `${BROWSER_CLASS_PREFIX}${detectBrowser(ua)}`,
    `${OS_CLASS_PREFIX}${detectOs(ua)}`,
    `${DEVICE_CLASS_PREFIX}${detectDeviceType(ua)}`,
  ];

  if (hasTouchInput()) classes.push("is-touch");
  if (isIosDevice(ua)) classes.push("is-ios");

  return classes;
}

/**
 * Adds device/browser/OS classes to `document.body`. Safe to call once at startup.
 */
export function initDeviceBrowserClasses() {
  if (typeof document === "undefined" || !document.body) return;

  const classes = getDeviceBrowserBodyClasses();
  document.body.classList.add(...classes);
}
